import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { formatErr } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { getProductById } from "../lib/firestoreProducts";
import ImageCarousel from "../components/ImageCarousel";

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { user, setLoginOpen } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getProductById(id)
      .then((data) => {
        if (!data) {
          navigate("/shop");
          return;
        }
        setProduct(data);
      })
      .catch(() => navigate("/shop"));
  }, [id, navigate]);

  const onAdd = async () => {
    if (!user) {
      sessionStorage.setItem("cns_login_intent", "cart");
      setLoginOpen(true);
      return;
    }
    setAdding(true);
    try {
      await addItem(product.id, qty);
      toast.success(`${product.name} added`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setAdding(false);
    }
  };

  if (!product) return <div className="pt-32 px-6 md:px-12">Loading…</div>;

  return (
    <div className="pt-20 md:pt-24" data-testid="product-page">
      <div className="grid md:grid-cols-2">
        <ImageCarousel primaryImage={product.image_url} galleryImages={product.gallery} />
        <div className="p-8 md:p-16 lg:p-20 flex flex-col justify-center">
          <Link to="/shop" className="label-eyebrow text-black/50 hover:text-black w-fit mb-8">← Back to shop</Link>
          <div className="label-eyebrow">{product.category} · Edition MMXXVI</div>
          <h1 data-testid="product-name" className="font-display text-4xl md:text-6xl tracking-tighter mt-3 leading-[0.95]">
            {product.name}
          </h1>
          <div data-testid="product-price" className="font-display text-3xl md:text-4xl mt-8">{`₹${product.price.toFixed(2)}`}</div>
          <p className="mt-8 text-black/70 leading-relaxed max-w-md">{product.description}</p>
          {product.embroidery_detail && (
            <div className="mt-8 border-t border-black/10 pt-6 max-w-md">
              <div className="label-eyebrow text-black/50 mb-2">Embroidery detail</div>
              <p className="text-sm">{product.embroidery_detail}</p>
            </div>
          )}

          <div className="mt-12 flex items-center gap-4 max-w-md">
            <div className="flex items-center border border-black h-12">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrease"
                className="w-12 h-full flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                aria-label="Decrease"
              >
                <Minus size={14} />
              </button>
              <span data-testid="qty-value" className="w-12 text-center font-display">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-increase"
                className="w-12 h-full flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                aria-label="Increase"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              onClick={onAdd}
              disabled={adding}
              data-testid="add-to-cart-btn"
              className="flex-1 h-12 bg-black text-white text-sm tracking-wide hover:bg-[#C9A961] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {adding ? "Adding…" : "Add to bag"} <ArrowUpRight size={14} />
            </button>
          </div>

          <div className="mt-10 text-xs text-black/50 max-w-md">
            Stock: {product.stock} · Numbered piece included.
          </div>
        </div>
      </div>
    </div>
  );
}
